import React, { useCallback, useEffect } from 'react';
import decode from 'jwt-decode';
import { datadogLogs } from '@datadog/browser-logs';
import { ScreenContainer } from '@pinwheel/origami';
import { satisfies } from 'compare-versions';
import ModalHeader from '../components/ModalHeader';
import ContentFrame from '../LinkApiRouter/components/ContentFrame';
import { useAppDispatch, useAppSelector } from '../state';
import { selectFeatureFlags, selectLinkTokenPayload, selectSdk, selectSdkVersion, } from '../state/slices/slice-link-params';
import useLinkNavigate from '../hooks/useLinkNavigate';
import { selectViewedHomerIFrame, setViewedHomerIFrame, } from '../state/slices/slice-segment';
import { useGetLocalizedString } from '../hooks/useGetLocalizedString';
import { getParentOrigin, postParentWindowMessage, } from '../utils/post-message';
import Loading from '../LinkApiRouter/components/Loading';
import { useOpts } from '../context';
import useLinkSolution from '../hooks/useLinkSolution';
import { getSemverString } from '../utils';
var JWT_PREFIX = 'eyJ';
var isSupportedSdkVersion = function (sdk, sdkVersion) {
    var sdkVersionString = getSemverString(sdkVersion);
    if (!sdk || !sdkVersion || !sdkVersionString)
        return false;
    if (sdk === 'android' && satisfies(sdkVersionString, '>=3.4.0'))
        return true;
    if (sdk === 'ios' && satisfies(sdkVersionString, '>=3.3.0'))
        return true;
    if (sdk === 'react native' && satisfies(sdkVersionString, '>=3.4.0'))
        return true;
    return false;
};
var HomerIframe = function () {
    var linkTokenPayload = useAppSelector(selectLinkTokenPayload);
    var viewedHomerIFrame = useAppSelector(selectViewedHomerIFrame);
    var navigate = useLinkNavigate();
    var dispatch = useAppDispatch();
    var getLocalizedString = useGetLocalizedString();
    var linkSolution = useLinkSolution().linkSolution;
    var exit = useOpts().exit;
    var featureFlags = useAppSelector(selectFeatureFlags);
    var sdk = useAppSelector(selectSdk);
    var sdkVersion = useAppSelector(selectSdkVersion);
    var shouldLoadInSdk = featureFlags.homerMobileSdkEnabled &&
        isSupportedSdkVersion(sdk, sdkVersion);
    var homerExitedEventListener = useCallback(function (event) {
        if (!shouldLoadInSdk)
            return;
        if (!event.data)
            return;
        if (typeof event.data !== 'object')
            return;
        var dataType = event.data.type;
        var eventName = 'eventName' in event.data ? event.data.eventName : null;
        var parentOrigin = getParentOrigin();
        if (dataType === 'PINWHEEL_INTERNAL_COMM_DOWN' &&
            eventName === 'homer_exited') {
            if (parentOrigin && event.origin !== parentOrigin) {
                datadogLogs.logger.warn("Link received 'homer_exited' event from an untrusted origin (".concat(event.origin, ")"));
                return;
            }
            exit();
        }
    }, [exit, shouldLoadInSdk]);
    useEffect(function () {
        window.addEventListener('message', homerExitedEventListener);
        return function () {
            return window.removeEventListener('message', homerExitedEventListener);
        };
    }, [homerExitedEventListener]);
    useEffect(function () {
        if (shouldLoadInSdk && linkTokenPayload.homerUrl) {
            postParentWindowMessage({
                type: 'PINWHEEL_INTERNAL_COMM_UP',
                eventName: 'homer_open',
                payload: {
                    homerUrl: linkTokenPayload.homerUrl,
                },
            });
        }
    }, [linkTokenPayload.homerUrl, shouldLoadInSdk]);
    useEffect(function () {
        if (!linkTokenPayload.homerUrl) {
            datadogLogs.logger.error('HomerIframe.tsx: Homer URL was not passed in link token');
            // TODO: We can add a recovery mechanism, but that probably involves new endpoints
            navigate('/error');
        }
        var decodedHomerUrl = decode(
        // By this time, we know homerURL is defined
        // @ts-ignore
        linkTokenPayload.homerUrl.substring(
        // @ts-ignore
        linkTokenPayload.homerUrl.indexOf(JWT_PREFIX)));
        // If the token expired before landing on the Bill Navigator page for the first time
        // throw an error. If the token expired AFTER landing on the page, then it doesn't matter
        // because the session was already initialized.
        if (!viewedHomerIFrame &&
            decodedHomerUrl.exp &&
            decodedHomerUrl.exp * 1000 < Date.now()) {
            datadogLogs.logger.error('HomerIframe.tsx: Homer URL has expired');
            // TODO: We can add a recovery mechanism, but that probably involves new endpoints
            navigate('/error');
        }
        // Now that the user has seen this page and validly loaded the URL, we don't
        // need to care about the expiration anymore
        dispatch(setViewedHomerIFrame());
    }, [linkTokenPayload, navigate, dispatch, viewedHomerIFrame]);
    if (shouldLoadInSdk) {
        // Show a loader since the Homer SDK will overlay on top once loaded
        return (React.createElement(ScreenContainer, { "data-testid": "bill-navigator-sdk-loader" },
            React.createElement(Loading, null)));
    }
    var title = linkSolution && linkSolution === 'Switch Kit'
        ? getLocalizedString('SWITCH_KIT')
        : getLocalizedString('BILL_NAVIGATOR');
    return (React.createElement(ScreenContainer, { "data-testid": "bill-navigator-iframe" },
        React.createElement(ModalHeader, { useThinHeader: true, hideBackButton: true, title: title }),
        React.createElement(ContentFrame, { padding: "0" },
            React.createElement("iframe", { title: title, src: linkTokenPayload.homerUrl, style: { width: '100%', height: '100%', border: 'none' } }))));
};
export default HomerIframe;
